import { useEffect } from "react";
import { Button, Col, Form, Radio, Row, Select, Upload, Image as AntImage, Input } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { UploadOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form/Form";
import { filter, keys, map, isEmpty } from "lodash";
import QuillEditor from "react-quill";

import "react-quill/dist/quill.snow.css";

const MAX_IMAGE_SIZE = 10,
  MAX_DATA_IMAGE_SIZE = 1;

const COVER_OPTIONS = map(Array(MAX_IMAGE_SIZE).fill(""), (_, index) => ({
  label: index + 1,
  value: index + 1,
}));

type ProductProps = {
  form: FormInstance;
  product?: {
    id: number;
    merchantId: number;
    isSales: boolean;
    // brand: {
    //   id: number;
    //   name: string;
    //   types: string[];
    // };
    brandId: string | number;
    type: string;
    modal: {};
    title: string;
    years: number;
    price: number;
    tags: string[];
    color?: string;
    kilometers: number;
    imageUrl: string;
    creator: {
      id: number;
      name: {};
      email: string;
    };
    imageIds: [];
    travelLicenseImagePath?: File[] | string;
    appraisalImagePath?: File[] | string;
    memo?: string;
  };
  formOptions: { brand: any; modal: any; tag: any };
};

export function ProductForm(props: ProductProps) {
  useEffect(() => {
    props.form.resetFields();
  }, [props.form]);

  const brandId = Form.useWatch("brandId", props.form) || [];
  const imageIds = Form.useWatch("imageIds", props.form);
  const travelLicenseImagePath = Form.useWatch("travelLicenseImagePath", props.form);
  const appraisalImagePath = Form.useWatch("appraisalImagePath", props.form);

  const getFile = (formItemKey: string) => {
    return props.form.getFieldValue(formItemKey) || [];
  };

  return (
    <Form className="product-form" form={props.form} initialValues={props.product || {}}>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Row gutter={12}>
        <Col span="12">
          <Form.Item label="是否上架" name="isSales" rules={[{ required: true, message: "請輸入是否上架" }]}>
            <Radio.Group
              options={[
                { label: "上架", value: true },
                { label: "下架", value: false },
              ]}
            />
          </Form.Item>

          <Form.Item label="標題" name="title" rules={[{ required: true, message: "請輸入標題" }]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="車廠"
            name="brandId"
            rules={[
              { required: true, message: "請選擇車廠" },
              {
                validator: async (rule, value) => {
                  value = value ?? [];
                  const isNewBrand =
                    filter(Object.values(props.formOptions.brand), (item: any) => String(item.id) === brandId[0])
                      .length === 0;

                  if (isNewBrand && /^[0-9]+$/.test(value[0])) {
                    throw new Error("不允許純數字");
                  }
                },
              },
            ]}
          >
            <Select
              mode="tags"
              maxCount={1}
              options={map(keys(props.formOptions.brand), (brandKey: any) => ({
                label: props.formOptions.brand[brandKey].name,
                value: String(props.formOptions.brand[brandKey].id),
              }))}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              onChange={value => {
                props.form.setFieldValue("brandId", value);
                props.form.setFieldValue("type", null);
              }}
            />
          </Form.Item>

          <Form.Item label="車款" name="type" rules={[{ required: true, message: "請選擇車款" }]}>
            <Select
              mode="tags"
              maxCount={1}
              options={map(props.formOptions.brand[brandId]?.types || [], (brandType: string) => ({
                label: brandType,
                value: brandType,
              }))}
            />
          </Form.Item>

          <Form.Item label="車種" name="modal" rules={[{ required: true, message: "請輸入車種" }]}>
            <Select mode="tags" maxCount={1} options={props.formOptions.modal} />
          </Form.Item>

          <Form.Item label="車色" name="color">
            <Input />
          </Form.Item>

          <Form.Item
            label="車牌"
            name="licensePlate"
            rules={[
              { required: true, message: "請輸入車牌" },
              {
                validator: async (rule, value) => {
                  if (!!value && !/^[A-Z\d]{1,4}-[A-Z\d]{1,6}$/.test(value)) {
                    throw new Error("請輸入正確的車牌格式（例如：AAA-1111）");
                  }
                },
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="里程"
            name="kilometers"
            rules={[
              { required: true, message: "請輸入里程" },
              {
                validator: async (rule, value) => {
                  value = String(Number(value));
                  if (/^[0-9]+\.[0-9]+$/.test(value)) {
                    throw new Error("不允許小數");
                  }
                },
              },
            ]}
          >
            <Input type="number" onWheel={e => e.currentTarget.blur()} />
          </Form.Item>

          <Form.Item label="標籤" name="tags">
            <Select mode="tags" options={props.formOptions.tag} />
          </Form.Item>

          <Form.Item
            label="出廠年份"
            name="years"
            rules={[
              { required: true, message: "請輸入出廠年份" },
              {
                validator: async (rule, value) => {
                  value = String(Number(value));
                  if (/^[0-9]+\.[0-9]+$/.test(value)) {
                    throw new Error("不允許小數");
                  } else if (!/^\d{4}$/.test(value)) {
                    throw new Error("年份需為4位數");
                  }
                },
              },
            ]}
          >
            <Input type="number" onWheel={e => e.currentTarget.blur()} />
          </Form.Item>

          <Form.Item
            label="價錢"
            name="price"
            rules={[
              { required: true, message: "請輸入價錢" },
              {
                validator: async (rule, value) => {
                  value = String(Number(value));

                  if (value <= 0) {
                    throw new Error("價錢需大於1");
                  } else if (/^[0-9]+\.[0-9]+$/.test(value)) {
                    throw new Error("不允許小數");
                  } else if (!/^\d{1,9}$/.test(value)) {
                    throw new Error("位數需小於9位");
                  }
                },
              },
            ]}
          >
            <Input type="number" onWheel={e => e.currentTarget.blur()} />
          </Form.Item>

          <Form.Item
            className="photo-serial"
            label="照片"
            name="imageIds"
            valuePropName="imageIds"
            getValueFromEvent={() => getFile("imageIds")}
            rules={[
              {
                required: true,
                message: "請選擇照片",
              },
              () => ({
                async validator(_, value) {
                  let element: File;
                  value = value ?? [];

                  if (value.length > MAX_IMAGE_SIZE) {
                    return Promise.reject(new Error(`照片總數需小於${MAX_IMAGE_SIZE}張`));
                  }

                  for (let index = 0; index < value.length; index++) {
                    element = value[index];
                    // const imageInfo: any = await loadImage(element);

                    if (element instanceof File) {
                      if (element.size > 1048576) {
                        return Promise.reject(new Error(`檔案太大，需小於 1MB（${element.name}）`));
                      }
                    } else if (!("url" in element)) {
                      return Promise.reject(new Error(`檔案讀取有誤`));
                    }
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Upload
              accept=".jpg,.png"
              listType="picture-card"
              multiple
              beforeUpload={async (file: File) => {
                const imageList = props.form.getFieldValue("imageIds") || [];
                props.form.setFieldValue("imageIds", imageList.concat(file));

                // 因為默認會有action 所以一定要 return false 不然會404
                return false;
              }}
              onPreview={onPreview}
              onRemove={(file: UploadFile) => {
                const imageList = props.form.getFieldValue("imageIds") || [];
                props.form.setFieldValue(
                  "imageIds",
                  imageList.filter((item: UploadFile) => item.uid !== file.uid)
                );
              }}
              defaultFileList={imageIds}
            >
              <Button icon={<UploadOutlined />}>選擇</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="封面照"
            name="coverImageId"
            // tooltip="請選擇上方的第幾張照片要做為封面照"
            extra="請選擇上方的第幾張照片要做為封面照"
            rules={[
              { required: true, message: "請選擇封面照" },
              {
                validator: async (rule, value) => {
                  const imageListLength = (props.form.getFieldValue("imageIds") || [])?.length;
                  if (value > imageListLength) {
                    throw new Error("無效的照片編號");
                  }
                },
              },
            ]}
          >
            <Select options={COVER_OPTIONS} />
            {/* <Select options={COVER_OPTIONS} suffixIcon={<p>張照片為封面照</p>} /> */}
          </Form.Item>

          <Form.Item
            label="行照照片"
            name="travelLicenseImagePath"
            valuePropName="travelLicenseImagePath"
            getValueFromEvent={() => getFile("travelLicenseImagePath")}
            rules={[
              {
                required: true,
                message: "請選擇行照照片",
              },
              () => ({
                async validator(_, value) {
                  let element: File;
                  value = value ?? [];

                  if (value.length > MAX_DATA_IMAGE_SIZE) {
                    return Promise.reject(new Error(`行照照片總數只能${MAX_DATA_IMAGE_SIZE}張`));
                  }

                  for (let index = 0; index < value.length; index++) {
                    element = value[index];
                    // const imageInfo: any = await loadImage(element);

                    if (element instanceof File) {
                      if (element.size > 1048576) {
                        return Promise.reject(new Error(`檔案太大，需小於 1MB（${element.name}）`));
                      }
                    } else if (!("url" in element)) {
                      return Promise.reject(new Error(`檔案讀取有誤`));
                    }
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Upload
              accept=".jpg,.png"
              listType="picture-card"
              beforeUpload={async (file: File) => {
                const imageList = props.form.getFieldValue("travelLicenseImagePath") || [];
                props.form.setFieldValue("travelLicenseImagePath", imageList.concat(file));

                // 因為默認會有action 所以一定要 return false 不然會404
                return false;
              }}
              onPreview={onPreview}
              onRemove={(file: UploadFile) => {
                const imageList = props.form.getFieldValue("travelLicenseImagePath") || [];
                props.form.setFieldValue(
                  "travelLicenseImagePath",
                  imageList.filter((item: UploadFile) => item.uid !== file.uid)
                );
              }}
              defaultFileList={travelLicenseImagePath}
            >
              <Button icon={<UploadOutlined />}>選擇</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="車輛鑑定書"
            name="appraisalImagePath"
            valuePropName="appraisalImagePath"
            getValueFromEvent={() => getFile("appraisalImagePath")}
            rules={[
              () => ({
                async validator(_, value) {
                  let element: File;
                  value = value ?? [];

                  if (value.length > MAX_DATA_IMAGE_SIZE) {
                    return Promise.reject(new Error(`車輛鑑定書只能${MAX_DATA_IMAGE_SIZE}張`));
                  }

                  for (let index = 0; index < value.length; index++) {
                    element = value[index];
                    // const imageInfo: any = await loadImage(element);

                    if (element instanceof File) {
                      if (element.size > 1048576) {
                        return Promise.reject(new Error(`檔案太大，需小於 1MB（${element.name}）`));
                      }
                    } else if (!("url" in element)) {
                      return Promise.reject(new Error(`檔案讀取有誤`));
                    }
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Upload
              accept=".jpg,.png,.pdf"
              listType="picture-card"
              beforeUpload={async (file: File) => {
                const imageList = props.form.getFieldValue("appraisalImagePath") || [];
                props.form.setFieldValue("appraisalImagePath", imageList.concat(file));

                // 因為默認會有action 所以一定要 return false 不然會404
                return false;
              }}
              itemRender={(originNode, file: any, fileList, actions: any) =>
                file.url ? (
                  originNode
                ) : (
                  <div className="ant-upload-list-item">
                    {file.name && (
                      <AntImage
                        src={file.thumbUrl}
                        fallback={
                          file.name.substring(file.name.length - 4) === ".pdf"
                            ? "https://cdn-icons-png.flaticon.com/512/201/201153.png"
                            : "https://cdn-icons-png.flaticon.com/512/44/44289.png"
                        }
                        preview={false}
                        alt={file.name}
                      />
                    )}
                    <div className="ant-upload-list-item-actions d-flex mt-1">
                      {!isEmpty(file.originFileObj) && (
                        <Button
                          style={{ background: "transparent" }}
                          shape="circle"
                          size="small"
                          icon={<EyeOutlined />}
                          type="link"
                          href={URL.createObjectURL(file.originFileObj)}
                          target="_blank"
                        />
                      )}
                      <Button
                        style={{ background: "transparent" }}
                        className="ant-btn ant-btn-text ant-btn-color-default ant-btn-variant-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-action"
                        shape="circle"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => actions.remove()}
                      />
                    </div>
                  </div>
                )
              }
              onPreview={onPreview}
              onRemove={(file: UploadFile) => {
                const imageList = props.form.getFieldValue("appraisalImagePath") || [];
                props.form.setFieldValue(
                  "appraisalImagePath",
                  imageList.filter((item: UploadFile) => item.uid !== file.uid)
                );
              }}
              defaultFileList={appraisalImagePath}
            >
              <Button icon={<UploadOutlined />}>選擇</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="重點文字" name="highlight" rules={[{ required: true, message: "請填寫重點文字" }]}>
            <Input />
          </Form.Item>
        </Col>

        <Col span="12">
          {/* <Form.Item label="車輛介紹" name="intro">
            <TextEditor />
          </Form.Item> */}

          <Form.Item label="規格&配備" name="spec">
            {/* @ts-ignore */}
            <TextEditor />
          </Form.Item>

          <Form.Item label="車主故事" name="story">
            {/* @ts-ignore */}
            <TextEditor />
          </Form.Item>

          <Form.Item label="保固說明" name="warranty">
            {/* @ts-ignore */}
            <TextEditor />
          </Form.Item>

          <Form.Item label="MY CHA點評" name="comment">
            {/* @ts-ignore */}
            <TextEditor />
          </Form.Item>

          <Form.Item label="備註" name="memo">
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const onPreview = async (file: UploadFile) => {
  let src = file.url as string;
  if (!src) {
    src = await new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj as FileType);
      reader.onload = () => resolve(reader.result as string);
    });
  }

  if (src.substring(src.length - 4) === ".pdf") {
    window.open(src);
  } else {
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  }
};

const toolbarOptions = [
  [
    "bold",
    "italic",
    "underline",
    "strike",
    "link",
    { list: "ordered" },
    { list: "bullet" },
    { color: [] },
    { background: [] },
    { header: [1, 2, 3, 4, 5, 6, false] },
    "clean", // remove formatting button
  ],
];

const TextEditor: React.FC<{
  value: string;
  placeholder: string;
  onChange: (e: any) => void;
}> = ({ value, onChange, placeholder }) => {
  return (
    <div className="text-editor">
      <QuillEditor
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        modules={{
          toolbar: toolbarOptions,
        }}
      />
    </div>
  );
};
